import { Grid, Box } from '@mui/material';
import { memo, useMemo } from 'react';
import CollapsableWrapper from '@/DesignSystem/wrappers/CollapsableWrapper';
import JugadoresSuplenteTitularCard from './JugadoresSuplenteTitularCard';
import JugadorPredictCard from './JugadorPredictCard';
import { useAlineacionesContext } from '../hooks/useAlineacionesContext';

const TitularesYSuplentes = memo(
    ({
        lineup = [],
        defaultOpen = true,
        mb = 0,
        mode,
        events,
        estadisticasPartido,
        equipo,
        playerBoxWidth = null,
        teamId,
    }) => {
        // Obtener el editor seleccionado del contexto
        const { selectedEditorLineup } = useAlineacionesContext() || {};

        // Fusionar lineup con datos del editor si está seleccionado
        const finalLineup = useMemo(() => {
            // Si no hay editor seleccionado o no es el equipo correcto, usar lineup original
            if (!selectedEditorLineup || selectedEditorLineup.team !== teamId) {
                return lineup;
            }

            // Crear un mapa de players del editor por ID
            const editorPlayersMap = new Map(selectedEditorLineup.players?.map((p) => [p.id, p]) || []);

            // Fusionar: mantener todos los datos del jugador original pero usar grid y chance del editor
            return (
                lineup?.map((player) => {
                    const editorPlayer = editorPlayersMap.get(player.id);

                    if (editorPlayer) {
                        return {
                            ...player,
                            grid: editorPlayer.grid,
                            chance: editorPlayer.chance,
                            alternatives: editorPlayer.alternatives,
                        };
                    }

                    return player;
                }) || []
            );
        }, [lineup, selectedEditorLineup, teamId]);

        // Determinar si mostrar porcentajes
        const showPercentage = useMemo(() => {
            // Si hay editor seleccionado, usar su configuración usePercentage
            if (selectedEditorLineup && selectedEditorLineup.team === teamId) {
                return selectedEditorLineup.usePercentage;
            }
            // Si no hay editor seleccionado, mostrar porcentajes por defecto en modo predict
            return true;
        }, [selectedEditorLineup, teamId]);

        const { titulares, suplentes } = useMemo(() => {
            let titulares = [];
            let suplentes = [];

            switch (mode) {
                case 'predict':
                    suplentes = finalLineup
                        .filter((player) => !player?.grid || player.grid < 10 || player.grid > 60)
                        .sort((a, b) => Number(b.chance ?? 0) - Number(a.chance ?? 0));
                    titulares = finalLineup
                        .filter((player) => player?.grid >= 10 && player?.grid <= 60)
                        .sort((a, b) => Number(b.chance ?? 0) - Number(a.chance ?? 0));
                    break;

                case 'confirmed':
                case 'last':
                    suplentes = finalLineup
                        .filter((player) => !player?.grid || player.grid < 10 || player.grid > 60)
                        .sort((a, b) => (a.grid ?? 999) - (b.grid ?? 999));
                    titulares = finalLineup
                        .filter((player) => player?.grid >= 10 && player?.grid <= 60)
                        .sort((a, b) => (a.grid ?? 999) - (b.grid ?? 999));
                    break;

                default:
                    suplentes = finalLineup.filter(
                        (player) => !player?.grid || player.grid < 10 || player.grid > 60
                    );
                    titulares = finalLineup.filter((player) => player?.grid >= 10 && player?.grid <= 60);
                    break;
            }

            return { titulares, suplentes };
        }, [finalLineup, mode]);

        // Renderizado para modo predict
        if (mode === 'predict') {
            return (
                <CollapsableWrapper
                    title={`${equipo}: Suplentes`}
                    defaultOpen={defaultOpen}
                    mb={mb}
                    fontSizeTitle={14}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                        }}
                    >
                        {suplentes.map((player, index) => (
                            <JugadorPredictCard
                                key={`suplente-${player.id}-${index}`}
                                player={player}
                                mode={mode}
                                events={events}
                                playerBoxWidth={playerBoxWidth}
                                showPercentage={showPercentage}
                            />
                        ))}
                    </Box>
                </CollapsableWrapper>
            );
        }

        if (!titulares?.length && !suplentes?.length) {
            return null;
        }

        // Renderizado original para otros modos
        return (
            <CollapsableWrapper
                title={`${equipo}: Titulares y suplentes`}
                defaultOpen={defaultOpen}
                mb={mb}
                fontSizeTitle={14}
            >
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        {titulares.map((player, index) => (
                            <JugadoresSuplenteTitularCard
                                key={`${player.id}-${index}`}
                                player={player}
                                direction="row"
                                align="flex-start"
                                mode={mode}
                                events={events}
                                estadisticasPartido={estadisticasPartido}
                                showPercentage={showPercentage}
                            />
                        ))}
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        {suplentes.map((player, index) => (
                            <JugadoresSuplenteTitularCard
                                key={`${player.id}-${index}`}
                                player={player}
                                direction="row-reverse"
                                align="flex-end"
                                mode={mode}
                                events={events}
                                estadisticasPartido={estadisticasPartido}
                                showPercentage={showPercentage}
                            />
                        ))}
                    </Grid>
                </Grid>
            </CollapsableWrapper>
        );
    }
);

export default TitularesYSuplentes;
